import { getLocalTimeZone, today } from '@internationalized/date';
import type { PromoCode } from '@customerio/hydra-ui-api';
import type { CheckoutCoupon } from '@/features/billing';
import type { BillingVendor } from '@/services/auth/types';

/** The account fields the promo-checkout gate reads. */
export type RequiresCardCheckoutAccount = {
  // Optional because the API omits it for accounts whose column was never set. This gate only ever
  // asks "is it Ordway", and an absent vendor is not, so it needs no resolution — unlike the billing
  // service's vendor flags. See `services/billing/resolve-vendor.ts`.
  billing_vendor?: BillingVendor;
  ordway_id?: string;
  promo_codes?: PromoCode[];
};

/**
 * Whether the account redeemed a promo code on an Ordway plan but hasn't checked out with a card yet.
 * The promo onboarding endpoint creates the account without any Ordway id, so an empty Ordway id marks
 * it as not-yet-checked-out. When true, app access is blocked until checkout completes. Ports the Ember
 * account model's `requiresCardCheckout` getter (SPE-3567).
 *
 * Note the "not yet an Ordway customer" part is the empty-`ordway_id` check (as the Ember account
 * model defines `isOrdwayCustomer`), NOT the `billing_vendor === 'ordway'` sense used elsewhere — the
 * whole point of the gate is an Ordway-vendor account that has no Ordway customer provisioned yet.
 */
export function requiresCardCheckout(account: RequiresCardCheckoutAccount): boolean {
  const hasOrdwayId = account.ordway_id != null && account.ordway_id.length > 0;
  return account.billing_vendor === 'ordway' && !hasOrdwayId && (account.promo_codes?.length ?? 0) > 0;
}

/** The promo code the checkout summary prices against — the first (and only) code redeemed. */
export function redeemedPromoCode(account: RequiresCardCheckoutAccount): PromoCode | undefined {
  return account.promo_codes?.[0];
}

/**
 * Models the redeemed promo's discount as a percentage-off coupon so the checkout summary reflects the
 * discounted amount due. Overages are not discounted (the card covers them), matching how the signup
 * form applies coupons. Returns undefined when there is no promo. Ports the Ember checkout controller's
 * `buildPromoCoupon` (SPE-3874) — the signup form reads the Hydra coupon shape (snake_case
 * `percent_off` / `expiration_date`), so the discount is built here rather than left as a raw model.
 */
export function buildPromoCoupon(promo: PromoCode | undefined): CheckoutCoupon | undefined {
  if (promo == null) {
    return undefined;
  }

  const discountValue = promo.config?.discount_value ?? 0;
  const durationMonths = Number(promo.config?.duration_period);
  const safeDurationMonths = Number.isFinite(durationMonths) ? durationMonths : 0;
  const expirationDate = today(getLocalTimeZone()).add({ months: safeDurationMonths }).toString();

  return {
    name: `${discountValue}% off`,
    percent_off: discountValue,
    expiration_date: expirationDate,
  };
}
